import React from "react";
import {useSelector} from "react-redux";
import {keyframes} from "@emotion/react";
import {styled} from "@reside/ui";

import {dispatch} from "../../store/store";
import {DownloadEntry, DownloadStatus} from "../../models/downloadManager";

export const DownloadManager = () => {
  const downloads: DownloadEntry[] = useSelector(
    (state: any) => state.downloadManager.downloads,
  );

  if (downloads.length === 0) return null;

  return (
    <Panel>
      <PanelTitle>Downloads</PanelTitle>
      {downloads.map(entry => (
        <EntryRow key={entry.id}>
          <EntryIcon>
            {entry.status === "pending" && <Spinner />}
            {entry.status === "ready" && <ReadyIcon>✓</ReadyIcon>}
            {entry.status === "error" && <ErrorIcon>✕</ErrorIcon>}
          </EntryIcon>
          <EntryContent>
            <EntryLabel>{entry.label}</EntryLabel>
            <EntryStatus status={entry.status}>
              {entry.status === "pending" && "Preparing…"}
              {entry.status === "ready" && "Ready"}
              {entry.status === "error" && (entry.error ?? "Failed")}
            </EntryStatus>
          </EntryContent>
          {entry.status !== "pending" && (
            <CloseButton
              onClick={() => dispatch.downloadManager.remove(entry.id)}
              aria-label="Dismiss"
            >
              ×
            </CloseButton>
          )}
        </EntryRow>
      ))}
    </Panel>
  );
};

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const Panel = styled.div`
  position: fixed;
  bottom: 24px;
  left: 24px;
  width: 300px;
  max-height: 60vh;
  overflow-y: auto;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  z-index: 9999;
`;

const PanelTitle = styled.div`
  padding: 8px 16px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${({theme}) => theme.color.gray100};
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  position: sticky;
  top: 0;
  background: white;
`;

const EntryRow = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  gap: 10px;

  & + & {
    border-top: 1px solid rgba(0, 0, 0, 0.06);
  }
`;

const EntryIcon = styled.div`
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Spinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid #e0e0e0;
  border-top-color: ${({theme}) => theme.color.primary};
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
`;

const ReadyIcon = styled.span`
  font-size: 14px;
  font-weight: bold;
  color: #22a35a;
  line-height: 1;
`;

const ErrorIcon = styled.span`
  font-size: 14px;
  font-weight: bold;
  color: #d93025;
  line-height: 1;
`;

const EntryContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const EntryLabel = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${({theme}) => theme.color.gray100};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const EntryStatus = styled.div<{status: DownloadStatus}>`
  font-size: 12px;
  margin-top: 2px;
  color: ${({status}) =>
    status === "error" ? "#d93025" : status === "ready" ? "#22a35a" : "#999"};
`;

const CloseButton = styled.button`
  flex-shrink: 0;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 20px;
  line-height: 1;
  padding: 0 2px;
  color: #aaa;

  &:hover {
    color: #555;
  }
`;
